import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Camera,
  Sparkles,
  UploadCloud,
  Trash2,
  Store,
  Zap,
  Flame,
  Cpu,
  Info,
  Eye,
  Loader2,
  Tag,
  MapPin,
  FileText,
  BadgeCheck,
  ShieldCheck,
} from "lucide-react";
import {
  fetchBrands,
  fetchBrandModels,
  fetchCities,
  createListing,
  uploadMedia,
  addListingPhoto,
  listingReadiness,
  listingPublish,
  generateAutoTitle,
  generateAiDescription,
  type Brand,
  type Model,
  type City,
  type CreateListingInput,
  type ListingSpecInput,
  type AutoTitleRequest,
  type AiDescriptionRequest,
  type ConditionValue,
} from "../api/auth";
import { PageShell } from "../components/Page";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const CONDITION_OPTIONS: { value: ConditionValue; label: string; desc: string }[] = [
  { value: "USED", label: "Used / Pre-owned", desc: "Previously ridden in good running order" },
  { value: "NEW", label: "Brand New", desc: "0 km directly from distributor or factory" },
  { value: "RECONDITIONED_IMPORTED", label: "Reconditioned Import", desc: "Direct import, overhauled and certified" },
  { value: "REFURBISHED", label: "Refurbished", desc: "Restored to premium showroom condition" },
  { value: "DAMAGED_FOR_PARTS", label: "For Parts / Salvage", desc: "Sold for components or restoration" },
];

const TRANSMISSION_OPTIONS = [
  { value: "MANUAL", label: "Manual" },
  { value: "AUTOMATIC", label: "Automatic" },
  { value: "DCT", label: "Dual-Clutch (DCT)" },
];

const POPULAR_COLORS = [
  "Black", "Matte Black", "White", "Pearl White", "Red", "Candy Red",
  "Blue", "Metallic Blue", "Silver", "Grey", "Matte Grey", "Green",
  "Yellow", "Orange", "Gunmetal", "Bronze", "Gold"
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 2 - 1980 }, (_, i) => currentYear + 1 - i);

function displayModelName(brandName: string, modelName: string): string {
  const escaped = brandName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const stripped = modelName.replace(new RegExp(`^${escaped}\\s+`, "i"), "").trim();
  return stripped || modelName;
}

export default function AddBikePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Reference data
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  // Form State
  const [title, setTitle] = useState("");
  const [brandId, setBrandId] = useState("");
  const [modelId, setModelId] = useState("");
  const [customModelName, setCustomModelName] = useState("");
  const [useCustomModel, setUseCustomModel] = useState(false);
  const [condition, setCondition] = useState<ConditionValue>("USED");
  const [price, setPrice] = useState<string>("");
  const [negotiable, setNegotiable] = useState(true);
  const [modelYear, setModelYear] = useState<string>("");
  const [manufacturingYear, setManufacturingYear] = useState<string>("");
  const [cityId, setCityId] = useState("");
  const [areaText, setAreaText] = useState("");

  // Technical Specs
  const [powertrain, setPowertrain] = useState<"PETROL" | "ELECTRIC" | "HYBRID">("PETROL");
  const [engineCc, setEngineCc] = useState<string>("");
  const [mileageKmpl, setMileageKmpl] = useState<string>("");
  const [odometerKm, setOdometerKm] = useState<string>("");
  const [fuelSystem, setFuelSystem] = useState("EFI");
  const [transmission, setTransmission] = useState<"MANUAL" | "AUTOMATIC" | "DCT">("MANUAL");
  const [color, setColor] = useState("");
  const [motorPowerKw, setMotorPowerKw] = useState<string>("");
  const [batteryCapacityKwh, setBatteryCapacityKwh] = useState<string>("");
  const [batteryRangeKm, setBatteryRangeKm] = useState<string>("");

  // Step 2 & Photos
  const [description, setDescription] = useState("");
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI & Submission Status
  const [aiLoading, setAiLoading] = useState<"title" | "description" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [createdListingId, setCreatedListingId] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const [blockingIssues, setBlockingIssues] = useState<string[]>([]);

  // Selected entities
  const selectedBrand = brands.find((b) => b.id === brandId) ?? null;
  const selectedModel = models.find((m) => m.id === modelId) ?? null;

  // Initial load
  useEffect(() => {
    (async () => {
      setBrandLoading(true);
      try {
        setBrands(await fetchBrands());
      } catch {
        toast.error("Could not load motorcycle brands");
      } finally {
        setBrandLoading(false);
      }
    })();

    (async () => {
      setCityLoading(true);
      try {
        const loaded = await fetchCities();
        setCities(loaded);
        if (loaded.length > 0) {
          const dhaka = loaded.find((c) => c.slug === "dhaka" || c.name.toLowerCase() === "dhaka");
          if (dhaka) {
            setCityId(dhaka.id);
          }
        }
      } catch (err) {
        console.error("Could not load cities:", err);
        toast.error("Could not load cities");
      } finally {
        setCityLoading(false);
      }
    })();
  }, []);

  // Fetch models on brand change
  useEffect(() => {
    let active = true;
    if (!selectedBrand) {
      setModels([]);
      setModelId("");
      return;
    }

    setModelLoading(true);
    fetchBrandModels(selectedBrand.slug)
      .then((rows) => {
        if (active) {
          setModels(rows);
          setModelId("");
        }
      })
      .catch(() => {
        if (active) setModels([]);
      })
      .finally(() => {
        if (active) setModelLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedBrand]);

  // Autofill specs when catalog model changes
  useEffect(() => {
    if (!selectedModel || !selectedBrand) return;

    const isElec = selectedModel.category.startsWith("ELECTRIC");
    setPowertrain(isElec ? "ELECTRIC" : "PETROL");

    if (isElec) {
      if (selectedModel.motorPowerKw != null) setMotorPowerKw(String(selectedModel.motorPowerKw));
      if (selectedModel.batteryCapacityKwh != null) setBatteryCapacityKwh(String(selectedModel.batteryCapacityKwh));
      setTransmission("AUTOMATIC");
      setEngineCc("");
    } else {
      if (selectedModel.engineCc != null) setEngineCc(String(selectedModel.engineCc));
      if (selectedModel.gearbox) {
        setTransmission(/auto|cvt|dct/i.test(selectedModel.gearbox) ? "AUTOMATIC" : "MANUAL");
      }
    }

    if (selectedModel.mileageKmpl != null) setMileageKmpl(String(selectedModel.mileageKmpl));

    const defaultYear = selectedModel.yearTo ?? selectedModel.yearFrom ?? currentYear;
    if (!modelYear) setModelYear(String(defaultYear));
    if (!manufacturingYear) setManufacturingYear(String(defaultYear));

    // Auto-generate title if currently empty or default
    const genTitle = `${selectedBrand.name} ${displayModelName(selectedBrand.name, selectedModel.name)}, ${defaultYear}`;
    if (!title || title.includes(selectedBrand.name)) {
      setTitle(genTitle);
    }
  }, [selectedModel, selectedBrand]);

  // Photo handlers
  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const validFiles = files.filter((f) => f.type.startsWith("image/"));
    if (validFiles.length < files.length) {
      toast.warning("Only image files are accepted");
    }

    const nextFiles = [...photoFiles, ...validFiles];
    setPhotoFiles(nextFiles);

    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemovePhoto = (index: number) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSetCoverPhoto = (index: number) => {
    if (index === 0) return;
    const nextFiles = [...photoFiles];
    const [movedFile] = nextFiles.splice(index, 1);
    nextFiles.unshift(movedFile);
    setPhotoFiles(nextFiles);

    const nextPreviews = [...photoPreviews];
    const [movedPreview] = nextPreviews.splice(index, 1);
    nextPreviews.unshift(movedPreview);
    setPhotoPreviews(nextPreviews);

    toast.info("Cover photo updated");
  };

  // AI Title
  const handleGenerateTitle = useCallback(async () => {
    if (!selectedBrand) {
      toast.error("Please select a brand first");
      return;
    }
    if (!modelId && !customModelName) {
      toast.error("Please pick a model or enter custom model name");
      return;
    }

    setAiLoading("title");
    try {
      const req: AutoTitleRequest = {
        brandId: selectedBrand.id,
        modelId: modelId || undefined,
        customModelName: customModelName || undefined,
        modelYear: modelYear ? Number(modelYear) : currentYear,
        condition,
        listingType: "DEALER_INVENTORY",
      };
      const res = await generateAutoTitle(req);
      if (res.title) {
        setTitle(res.title);
        toast.success("Title generated");
      }
    } catch {
      // Fallback
      const modelStr = selectedModel ? displayModelName(selectedBrand.name, selectedModel.name) : customModelName;
      const fallback = `${selectedBrand.name} ${modelStr}${modelYear ? `, ${modelYear}` : ""}`;
      setTitle(fallback);
      toast.info("Generated standard title");
    } finally {
      setAiLoading(null);
    }
  }, [selectedBrand, modelId, selectedModel, customModelName, modelYear, condition]);

  // AI Description
  const handleGenerateDescription = useCallback(async () => {
    if (!selectedBrand) {
      toast.error("Please select a brand and model first");
      return;
    }
    if (!modelId && !customModelName) {
      toast.error("Please specify the model name");
      return;
    }

    setAiLoading("description");
    try {
      const spec: ListingSpecInput = {
        powertrain,
        transmission,
        engineCc: powertrain !== "ELECTRIC" && engineCc ? Number(engineCc) : undefined,
        mileageKmpl: mileageKmpl ? Number(mileageKmpl) : undefined,
        odometerKm: odometerKm ? Number(odometerKm) : undefined,
        motorPowerKw: powertrain !== "PETROL" && motorPowerKw ? Number(motorPowerKw) : undefined,
        batteryCapacityKwh: powertrain !== "PETROL" && batteryCapacityKwh ? Number(batteryCapacityKwh) : undefined,
        batteryRangeKm: powertrain !== "PETROL" && batteryRangeKm ? Number(batteryRangeKm) : undefined,
        fuelSystem: fuelSystem || undefined,
        color: color || undefined,
      };

      const req: AiDescriptionRequest = {
        brandId: selectedBrand.id,
        modelId: modelId || undefined,
        customModelName: customModelName || undefined,
        modelYear: modelYear ? Number(modelYear) : currentYear,
        condition,
        listingType: "DEALER_INVENTORY",
        spec,
        price: price ? Number(price) : undefined,
        areaText: areaText || undefined,
      };

      const res = await generateAiDescription(req);
      if (res.description) {
        setDescription(res.description);
        toast.success("Showroom description generated with AI");
      }
    } catch {
      toast.error("Could not reach AI description service");
    } finally {
      setAiLoading(null);
    }
  }, [
    selectedBrand,
    modelId,
    selectedModel,
    customModelName,
    modelYear,
    condition,
    powertrain,
    transmission,
    engineCc,
    mileageKmpl,
    odometerKm,
    motorPowerKw,
    batteryCapacityKwh,
    batteryRangeKm,
    fuelSystem,
    color,
    price,
    areaText,
  ]);

  // Step 1 validation
  const validateStep1 = (): boolean => {
    if (!title.trim()) {
      toast.error("Please provide a title for the bike");
      return false;
    }
    if (!brandId) {
      toast.error("Please select a brand");
      return false;
    }
    if (!modelId && !customModelName.trim()) {
      toast.error("Please choose a model or enter the model name");
      return false;
    }
    if (!price || Number(price) <= 0) {
      toast.error("Please enter a valid price in BDT");
      return false;
    }
    if (!cityId) {
      toast.error("Please select a city location");
      return false;
    }
    if (powertrain === "PETROL" && (!engineCc || Number(engineCc) <= 0)) {
      toast.error("Please provide engine displacement (cc) for combustion bike");
      return false;
    }
    if (powertrain === "ELECTRIC" && !motorPowerKw && !batteryCapacityKwh) {
      toast.error("Electric bikes require motor power (kW) or battery capacity (kWh)");
      return false;
    }
    return true;
  };

  // Step 2 validation
  const validateStep2 = (): boolean => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error("Please provide a description of at least 10 characters");
      return false;
    }
    if (photoFiles.length < 3) {
      toast.warning("Upload at least 3 photos to publish live (you have " + photoFiles.length + ")");
    }
    return true;
  };

  // Final submit handler
  const handleCreateAndPublish = async (shouldPublishLive: boolean) => {
    if (!validateStep1() || !validateStep2()) return;

    setSubmitting(true);
    setUploadProgress("Creating showroom listing...");

    try {
      // Build compliant spec according to BR2
      const spec: ListingSpecInput = {
        powertrain,
        transmission,
        fuelSystem: powertrain !== "ELECTRIC" ? fuelSystem || undefined : undefined,
        color: color || undefined,
        odometerKm: odometerKm ? Number(odometerKm) : undefined,
      };

      if (powertrain === "ELECTRIC") {
        if (motorPowerKw) spec.motorPowerKw = Number(motorPowerKw);
        if (batteryCapacityKwh) spec.batteryCapacityKwh = Number(batteryCapacityKwh);
        if (batteryRangeKm) spec.batteryRangeKm = Number(batteryRangeKm);
      } else {
        if (engineCc) spec.engineCc = Number(engineCc);
        if (mileageKmpl) spec.mileageKmpl = Number(mileageKmpl);
      }

      // Build payload: Hardcoded listingType: DEALER_INVENTORY, contactPreference: CHAT_AND_CALL, allowVideoCall: false, NO upazilaId
      const payload: CreateListingInput = {
        title: title.trim(),
        description: description.trim(),
        listingType: "DEALER_INVENTORY",
        condition,
        brandId,
        modelId: modelId || undefined,
        customModelName: !modelId ? customModelName.trim() : undefined,
        price: Number(price),
        currency: "BDT",
        negotiable,
        modelYear: modelYear ? Number(modelYear) : undefined,
        manufacturingYear: manufacturingYear ? Number(manufacturingYear) : undefined,
        cityId,
        areaText: areaText.trim() || undefined,
        contactPreference: "CHAT_AND_CALL",
        allowVideoCall: false,
        spec,
      };

      const listing = await createListing(payload);
      setCreatedListingId(listing.id);

      // Upload and attach media photos
      if (photoFiles.length > 0) {
        for (let i = 0; i < photoFiles.length; i++) {
          setUploadProgress(`Uploading photo ${i + 1} of ${photoFiles.length}...`);
          const asset = await uploadMedia(photoFiles[i]);
          await addListingPhoto(listing.id, {
            assetId: asset.id,
            position: i,
            isCover: i === 0,
          });
        }
      }

      // Check readiness
      setUploadProgress("Verifying readiness criteria...");
      const ready = await listingReadiness(listing.id);
      setBlockingIssues(ready.blocking);

      if (shouldPublishLive) {
        if (ready.canPublish) {
          setUploadProgress("Publishing to live showroom...");
          await listingPublish(listing.id);
          setPublished(true);
          toast.success("Motorcycle published live to showroom!");
        } else {
          toast.warning("Saved as draft. Resolve blocking items below to publish.");
        }
      } else {
        toast.success("Draft saved successfully!");
      }
    } catch (err: unknown) {
      toast.error((err as { message?: string })?.message ?? "Failed to save listing");
    } finally {
      setSubmitting(false);
      setUploadProgress("");
    }
  };

  return (
    <PageShell className="space-y-6 pb-12">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/80 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/vehicles/all")}
              className="gap-1.5 text-muted-foreground hover:text-foreground pl-0 pr-2 h-7"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Inventory</span>
            </Button>
            <span className="text-muted-foreground/40">/</span>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 gap-1.5 font-medium">
              <Store className="w-3 h-3" /> Dealer Inventory
            </Badge>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Add Vehicle to Inventory</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Create and publish a motorcycle, scooter, or moped listing in your showroom.
          </p>
        </div>

        {/* Stepper Tabs */}
        {!published && (
          <div className="inline-flex rounded-lg bg-muted p-1 border border-border/60 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                currentStep === 1
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-mono">1</span>
              <span>Details</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (validateStep1()) setCurrentStep(2);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                currentStep === 2
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-mono">2</span>
              <span>Media & Story</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (validateStep1() && validateStep2()) setCurrentStep(3);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                currentStep === 3
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-mono">3</span>
              <span>Review & Publish</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Success Screen ── */}
      {published ? (
        <Card className="border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="pt-8 pb-8 text-center flex flex-col items-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-foreground">Motorcycle is Live on BikerHat!</h2>
            <p className="text-sm text-muted-foreground max-w-md mt-1 mb-6">
              Your listing is published and immediately discoverable by prospective buyers on the marketplace.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => navigate("/vehicles/all")} className="gap-2">
                <Store className="w-4 h-4" /> View All Inventory
              </Button>
              {createdListingId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/vehicles/edit/${createdListingId}`)}
                >
                  Edit Bike
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setPublished(false);
                  setCreatedListingId(null);
                  setTitle("");
                  setPrice("");
                  setPhotoFiles([]);
                  setPhotoPreviews([]);
                  setCurrentStep(1);
                }}
              >
                Add Another Bike
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!published && (
        <div className="space-y-6">
          {/* ══════════════════════════════════════════════════════════════
              STEP 1: BIKE DETAILS & SPECIFICATIONS
          ══════════════════════════════════════════════════════════════ */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* 1. Identity & Pricing */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tag className="w-4 h-4 text-primary" /> Vehicle Identity & Pricing
                    </CardTitle>
                    <CardDescription>
                      Select the make, model, condition, and price in Bangladeshi Taka.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Title with AI auto-fill */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="title" className="text-xs font-semibold">
                          Listing Title <span className="text-rose-500">*</span>
                        </Label>
                        <button
                          type="button"
                          onClick={handleGenerateTitle}
                          disabled={aiLoading === "title" || !brandId}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium disabled:opacity-50 cursor-pointer"
                        >
                          {aiLoading === "title" ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="w-3.5 h-3.5" />
                          )}
                          <span>Generate Title</span>
                        </button>
                      </div>
                      <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Honda CBR 150R Repsol Edition, 2022"
                      />
                    </div>

                    {/* Brand & Model */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="brand" className="text-xs font-semibold mb-1.5 block">
                          Brand / Make <span className="text-rose-500">*</span>
                        </Label>
                        <select
                          id="brand"
                          value={brandId}
                          onChange={(e) => {
                            setBrandId(e.target.value);
                            setCustomModelName("");
                            setUseCustomModel(false);
                          }}
                          className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none transition-colors"
                        >
                          <option value="">{brandLoading ? "Loading brands..." : "Select Brand"}</option>
                          {brands.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <Label htmlFor="model" className="text-xs font-semibold block">
                            Model <span className="text-rose-500">*</span>
                          </Label>
                          <button
                            type="button"
                            onClick={() => setUseCustomModel(!useCustomModel)}
                            className="text-[11px] text-muted-foreground hover:text-foreground underline cursor-pointer"
                          >
                            {useCustomModel ? "Pick from catalog" : "Model not listed?"}
                          </button>
                        </div>

                        {useCustomModel ? (
                          <Input
                            placeholder="Enter custom model name"
                            value={customModelName}
                            onChange={(e) => {
                              setCustomModelName(e.target.value);
                              setModelId("");
                            }}
                          />
                        ) : (
                          <select
                            id="model"
                            value={modelId}
                            disabled={!brandId || modelLoading}
                            onChange={(e) => setModelId(e.target.value)}
                            className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none transition-colors disabled:opacity-50"
                          >
                            <option value="">
                              {!brandId
                                ? "Choose brand first"
                                : modelLoading
                                ? "Loading models..."
                                : "Select Model"}
                            </option>
                            {models.map((m) => (
                              <option key={m.id} value={m.id}>
                                {displayModelName(selectedBrand?.name ?? "", m.name)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* Model Year & Year of Manufacture: SELECT DROPDOWNS */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="modelYear" className="text-xs font-semibold mb-1.5 block">
                          Model Year
                        </Label>
                        <select
                          id="modelYear"
                          value={modelYear}
                          onChange={(e) => setModelYear(e.target.value)}
                          className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none transition-colors"
                        >
                          <option value="">Select Model Year</option>
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="mfgYear" className="text-xs font-semibold mb-1.5 block">
                          Year of Manufacture
                        </Label>
                        <select
                          id="mfgYear"
                          value={manufacturingYear}
                          onChange={(e) => setManufacturingYear(e.target.value)}
                          className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none transition-colors"
                        >
                          <option value="">Select Manufacturing Year</option>
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Condition selection */}
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Condition</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {CONDITION_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setCondition(opt.value)}
                            className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                              condition === opt.value
                                ? "border-primary bg-primary/5 ring-1 ring-primary/40 text-foreground"
                                : "border-border hover:border-border/80 bg-background text-muted-foreground"
                            }`}
                          >
                            <div className="font-semibold text-xs text-foreground">{opt.label}</div>
                            <div className="text-[11px] line-clamp-1 text-muted-foreground">{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price & Negotiable */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                      <div>
                        <Label htmlFor="price" className="text-xs font-semibold mb-1.5 block">
                          Price (BDT) <span className="text-rose-500">*</span>
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground">
                            ৳
                          </span>
                          <Input
                            id="price"
                            type="number"
                            min="0"
                            placeholder="e.g. 235000"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="pl-7"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col justify-end">
                        <label className="flex items-center gap-2.5 h-8 px-3 rounded-lg border border-input bg-muted/20 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={negotiable}
                            onChange={(e) => setNegotiable(e.target.checked)}
                            className="rounded border-input text-primary focus:ring-primary w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs font-medium text-foreground">Price is negotiable</span>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Technical Specifications */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-primary" /> Powertrain & Engineering Specs
                    </CardTitle>
                    <CardDescription>
                      Engine configuration, fuel delivery, transmission, and color.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Powertrain switch */}
                    <div>
                      <Label className="text-xs font-semibold mb-1.5 block">Powertrain Type</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setPowertrain("PETROL")}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                            powertrain === "PETROL"
                              ? "border-primary bg-primary/5 text-primary ring-1 ring-primary/40"
                              : "border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Flame className="w-3.5 h-3.5" />
                          <span>Petrol / Combustion</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPowertrain("ELECTRIC")}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                            powertrain === "ELECTRIC"
                              ? "border-primary bg-primary/5 text-primary ring-1 ring-primary/40"
                              : "border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Zap className="w-3.5 h-3.5" />
                          <span>Electric EV</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPowertrain("HYBRID")}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                            powertrain === "HYBRID"
                              ? "border-primary bg-primary/5 text-primary ring-1 ring-primary/40"
                              : "border-border text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Cpu className="w-3.5 h-3.5" />
                          <span>Hybrid</span>
                        </button>
                      </div>
                    </div>

                    {/* Petrol / Combustion Fields */}
                    {powertrain !== "ELECTRIC" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                        <div>
                          <Label htmlFor="engineCc" className="text-xs font-semibold mb-1.5 block">
                            Engine (cc) <span className="text-rose-500">*</span>
                          </Label>
                          <Input
                            id="engineCc"
                            type="number"
                            min="1"
                            max="3000"
                            placeholder="e.g. 155"
                            value={engineCc}
                            onChange={(e) => setEngineCc(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="mileage" className="text-xs font-semibold mb-1.5 block">
                            Mileage (km/l)
                          </Label>
                          <Input
                            id="mileage"
                            type="number"
                            min="0"
                            placeholder="e.g. 45"
                            value={mileageKmpl}
                            onChange={(e) => setMileageKmpl(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="odometer" className="text-xs font-semibold mb-1.5 block">
                            Odometer (km)
                          </Label>
                          <Input
                            id="odometer"
                            type="number"
                            min="0"
                            placeholder="e.g. 12500"
                            value={odometerKm}
                            onChange={(e) => setOdometerKm(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="fuelSystem" className="text-xs font-semibold mb-1.5 block">
                            Fuel System
                          </Label>
                          <select
                            id="fuelSystem"
                            value={fuelSystem}
                            onChange={(e) => setFuelSystem(e.target.value)}
                            className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
                          >
                            <option value="EFI">EFI (Electronic Fuel Injection)</option>
                            <option value="Carburetor">Carburetor</option>
                            <option value="Direct Injection">Direct Injection</option>
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="transmission" className="text-xs font-semibold mb-1.5 block">
                            Transmission
                          </Label>
                          <select
                            id="transmission"
                            value={transmission}
                            onChange={(e) => setTransmission(e.target.value as "MANUAL" | "AUTOMATIC" | "DCT")}
                            className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
                          >
                            {TRANSMISSION_OPTIONS.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="color" className="text-xs font-semibold mb-1.5 block">
                            Color
                          </Label>
                          <select
                            id="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="w-full h-8 px-2.5 py-1 rounded-lg border border-input bg-background text-sm focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
                          >
                            <option value="">Select or leave blank</option>
                            {POPULAR_COLORS.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : null}

                    {/* Electric Fields */}
                    {powertrain !== "PETROL" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                        <div>
                          <Label htmlFor="motorPower" className="text-xs font-semibold mb-1.5 block">
                            Motor Power (kW)
                          </Label>
                          <Input
                            id="motorPower"
                            type="number"
                            min="0"
                            placeholder="e.g. 6.5"
                            value={motorPowerKw}
                            onChange={(e) => setMotorPowerKw(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="batteryCap" className="text-xs font-semibold mb-1.5 block">
                            Battery Capacity (kWh)
                          </Label>
                          <Input
                            id="batteryCap"
                            type="number"
                            min="0"
                            placeholder="e.g. 3.2"
                            value={batteryCapacityKwh}
                            onChange={(e) => setBatteryCapacityKwh(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="range" className="text-xs font-semibold mb-1.5 block">
                            Range per Charge (km)
                          </Label>
                          <Input
                            id="range"
                            type="number"
                            min="0"
                            placeholder="e.g. 110"
                            value={batteryRangeKm}
                            onChange={(e) => setBatteryRangeKm(e.target.value)}
                          />
                        </div>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>

                {/* 3. Dealership Location (City & Area, NO UPAZILA) */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" /> Showroom / Vehicle Location
                    </CardTitle>
                    <CardDescription>
                      Where buyers can inspect or test ride this motorcycle.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-xs font-semibold mb-1.5 block">
                        City / District <span className="text-rose-500">*</span>
                      </Label>
                      <select
                        id="city"
                        value={cityId}
                        onChange={(e) => setCityId(e.target.value)}
                        className="w-full h-9 px-3 py-1.5 rounded-lg border border-input bg-background text-sm font-medium focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 outline-none transition-colors"
                      >
                        <option value="">{cityLoading ? "Loading cities..." : "Select City / District"}</option>
                        {cities.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} {c.nameBn ? `(${c.nameBn})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="area" className="text-xs font-semibold mb-1.5 block">
                        Area / Showroom Address
                      </Label>
                      <Input
                        id="area"
                        placeholder="e.g. Mirpur 10, Road 4 or Showroom 2"
                        value={areaText}
                        onChange={(e) => setAreaText(e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar Info & Summary */}
              <div className="space-y-6">
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Info className="w-4 h-4 text-primary" /> Listing Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-xs">
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="font-semibold text-foreground">Dealer Inventory</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Make:</span>
                      <span className="font-medium text-foreground">{selectedBrand?.name ?? "—"}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Model:</span>
                      <span className="font-medium text-foreground">
                        {selectedModel ? displayModelName(selectedBrand?.name ?? "", selectedModel.name) : customModelName || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Year:</span>
                      <span className="font-medium text-foreground">{modelYear || "—"}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Price:</span>
                      <span className="font-semibold text-primary">
                        {price ? `৳ ${Number(price).toLocaleString()}` : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Contact:</span>
                      <span className="font-medium text-foreground">Chat & Phone Call</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-muted-foreground">Location:</span>
                      <span className="font-medium text-foreground">
                        {cities.find((c) => c.id === cityId)?.name ?? "—"}
                      </span>
                    </div>

                    <div className="pt-2">
                      <Button
                        type="button"
                        onClick={() => {
                          if (validateStep1()) setCurrentStep(2);
                        }}
                        className="w-full gap-2 font-semibold shadow-xs"
                      >
                        <span>Continue to Media & Story</span>
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Model Catalogue Specs Preview */}
                {selectedModel && (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xs font-semibold text-primary flex items-center gap-1.5">
                        <BadgeCheck className="w-3.5 h-3.5" /> Catalogue Reference Data
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1.5 text-xs text-muted-foreground">
                      <p>
                        <strong className="text-foreground">Category:</strong> {selectedModel.category}
                      </p>
                      {selectedModel.engineCc && (
                        <p>
                          <strong className="text-foreground">Displacement:</strong> {selectedModel.engineCc} cc
                        </p>
                      )}
                      {selectedModel.yearFrom && (
                        <p>
                          <strong className="text-foreground">Production:</strong> {selectedModel.yearFrom} –{" "}
                          {selectedModel.yearTo ?? "present"}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              STEP 2: STORY & GALLERY (PHOTOS & AI DESCRIPTION)
          ══════════════════════════════════════════════════════════════ */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Description Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" /> Listing Description
                      </CardTitle>
                      <CardDescription>
                        Detail the motorcycle's condition, paperwork status, upgrades, and test-ride availability.
                      </CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateDescription}
                      disabled={aiLoading === "description"}
                      className="gap-1.5 font-medium text-xs h-8 border-primary/30 text-primary hover:bg-primary/5"
                    >
                      {aiLoading === "description" ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5" />
                      )}
                      <span>Write with AI</span>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Textarea
                    rows={6}
                    placeholder="Provide authentic details about maintenance, ownership, tire condition, registration expiration, and showroom timings..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {description.length} characters (minimum 10 required)
                  </p>
                </CardContent>
              </Card>

              {/* Photo Upload Zone */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Camera className="w-4 h-4 text-primary" /> Showroom Photos
                      </CardTitle>
                      <CardDescription>
                        Upload clear photos. Minimum 3 required to publish. First photo is used as cover.
                      </CardDescription>
                    </div>
                    <Badge
                      variant={photoFiles.length >= 3 ? "default" : "secondary"}
                      className={
                        photoFiles.length >= 3
                          ? "bg-emerald-600 text-white"
                          : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                      }
                    >
                      {photoFiles.length} / 3 required photos
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Dropzone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-muted/40 rounded-xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleAddPhotos}
                      className="hidden"
                    />
                    <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-1">
                      <UploadCloud className="w-6 h-6" />
                    </div>
                    <div className="font-semibold text-sm text-foreground">
                      Click to browse or drag and drop photos
                    </div>
                    <p className="text-xs text-muted-foreground max-w-sm">
                      Recommended: High resolution front 3/4, side profile, rear, odometer cluster, and engine bay.
                    </p>
                  </div>

                  {/* Photo Gallery Grid */}
                  {photoPreviews.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3 pt-2">
                      {photoPreviews.map((previewUrl, i) => (
                        <div
                          key={i}
                          className="group relative aspect-4/3 rounded-lg overflow-hidden border border-border bg-muted/40 shadow-xs"
                        >
                          <img
                            src={previewUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />

                          {/* Cover badge */}
                          {i === 0 ? (
                            <span className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded shadow-xs">
                              Cover
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSetCoverPhoto(i)}
                              className="absolute top-1.5 left-1.5 bg-background/80 hover:bg-background text-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              Set Cover
                            </button>
                          )}

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(i)}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-700 cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back to Details
                </Button>
                <Button
                  onClick={() => {
                    if (validateStep2()) setCurrentStep(3);
                  }}
                  className="gap-2"
                >
                  <span>Review & Summary</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              STEP 3: REVIEW & PUBLISH LIVE
          ══════════════════════════════════════════════════════════════ */}
          {currentStep === 3 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Showroom Live Card Preview */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="w-4 h-4 text-primary" /> Live Showroom Card Preview
                    </CardTitle>
                    <CardDescription>
                      This is how prospective motorcycle buyers will view your vehicle card.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-w-md rounded-xl border border-border/80 overflow-hidden bg-card shadow-sm">
                      <div className="relative aspect-16/10 bg-muted">
                        {photoPreviews.length > 0 ? (
                          <img
                            src={photoPreviews[0]}
                            alt="Cover"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-1">
                            <Camera className="w-8 h-8 opacity-40" />
                            <span className="text-xs">No cover photo uploaded</span>
                          </div>
                        )}
                        <span className="absolute top-2.5 left-2.5 bg-background/90 backdrop-blur-xs text-foreground text-xs font-semibold px-2 py-0.5 rounded shadow-xs">
                          {CONDITION_OPTIONS.find((c) => c.value === condition)?.label}
                        </span>
                        <span className="absolute bottom-2.5 right-2.5 bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded shadow-xs">
                          Dealer Inventory
                        </span>
                      </div>

                      <div className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-bold text-base text-foreground line-clamp-1">{title}</h3>
                          <div className="text-right">
                            <span className="text-lg font-black text-primary whitespace-nowrap">
                              ৳ {Number(price || 0).toLocaleString()}
                            </span>
                            {negotiable && (
                              <span className="block text-[10px] text-muted-foreground">Negotiable</span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground pt-1">
                          {modelYear && (
                            <span className="bg-muted px-2 py-0.5 rounded font-medium">{modelYear}</span>
                          )}
                          {engineCc && (
                            <span className="bg-muted px-2 py-0.5 rounded font-medium">{engineCc} cc</span>
                          )}
                          {powertrain && (
                            <span className="bg-muted px-2 py-0.5 rounded font-medium">{powertrain}</span>
                          )}
                          {cityId && (
                            <span className="bg-muted px-2 py-0.5 rounded font-medium flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {cities.find((c) => c.id === cityId)?.name}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground line-clamp-2 pt-1">
                          {description || "No description provided."}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Blocking Issues Alert if any */}
                {blockingIssues.length > 0 && (
                  <Card className="border-rose-500/20 bg-rose-50/50 dark:bg-rose-950/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-rose-600 dark:text-rose-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Checklist for Live Publishing
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-5 text-xs space-y-1 text-rose-700 dark:text-rose-300">
                        {blockingIssues.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Action Column */}
              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Publish Action</CardTitle>
                    <CardDescription>
                      Publish live to marketplace or save draft to inventory.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {uploadProgress && (
                      <div className="p-3 bg-muted rounded-lg text-xs flex items-center gap-2 font-medium">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span>{uploadProgress}</span>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={() => handleCreateAndPublish(true)}
                      disabled={submitting}
                      className="w-full gap-2 font-bold h-10 shadow-xs cursor-pointer"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      <span>Publish Live Now</span>
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleCreateAndPublish(false)}
                      disabled={submitting}
                      className="w-full cursor-pointer"
                    >
                      Save as Draft Only
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(2)}
                      className="w-full gap-2 text-xs cursor-pointer"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Back to Photos
                    </Button>
                  </CardContent>
                </Card>

                {/* Contact note */}
                <Card className="bg-muted/20">
                  <CardContent className="pt-4 text-xs space-y-2 text-muted-foreground">
                    <div className="flex items-center gap-2 font-semibold text-foreground">
                      <ShieldCheck className="w-4 h-4 text-emerald-600" /> Standard Dealership Settings
                    </div>
                    <p>• Inquiries route straight to your Partner Chat & Call queue.</p>
                    <p>• Listing type is fixed to Dealer Inventory.</p>
                    <p>• Video call screening disabled by default.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}
